import React from 'react';
import { Formik, Form, FormikValues } from 'formik';
import { QualityActionRow } from '~/components/Patient';
import { usePatientService } from '~/services';
import {
    buildQualitySubmissionAnswers,
    enumValue,
    gapIsAddressed,
    gapIsUnderReview,
    qualityGroupingValidation,
} from '~/util';

export type QualityFormChangeHandler = (
    id: number | string,
    value: null | boolean | string | number | Date
) => void;
export interface QualityGapFormProps {
    qualityGap: GapType;
    formId: string;
    patientId: number;
    onSubmitSuccess: () => void;
    setFormDirty: (x: boolean) => void;
    setSaving: (x: boolean) => void;
    statusFilter: number;
    setDisabled: (x: boolean) => void;
    isRestarting: boolean;
}
export function QualityGapForm({
    qualityGap,
    patientId,
    setSaving,
    setDisabled,
    onSubmitSuccess,
    setFormDirty,
    formId,
    statusFilter,
    isRestarting,
}: QualityGapFormProps) {
    const { updateQualityGap } = usePatientService();
    const [formValues, setFormValues] = React.useState({});
    const dirtyFlag = React.useRef(false);
    const submitted = React.useRef(false);

    const handleFormInputChange: QualityFormChangeHandler = (id, value) => {
        setFormValues((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    async function handleSave(values: FormikValues) {
        const responses = buildQualitySubmissionAnswers(values);
        if (responses.length === 0) return;

        try {
            setSaving(true);
            setDisabled(true);
            await updateQualityGap(
                qualityGap.id,
                patientId,
                {
                    submission_answer: { metadata: { responses } },
                    updated_at: qualityGap.updated_at,
                },
                // Explicitly pop-up ApiErrorResponse modal to handle backend validation errors
                { handledErrors: { 400: false } }
            );
            onSubmitSuccess();
        } catch (error) {
            setDisabled(false);
            setSaving(false);
        }
    }

    const readOnly =
        gapIsUnderReview(qualityGap) ||
        (gapIsAddressed(qualityGap) &&
            qualityGap.submission_answer?.status !== enumValue(['submission', 'status', 'New']) &&
            !isRestarting);

    return (
        <Formik
            enableReinitialize={!submitted.current}
            initialValues={formValues}
            validateOnChange
            validateOnBlur={submitted.current}
            onSubmit={handleSave}
            validate={(values) => qualityGroupingValidation(values, qualityGap.measure)}
        >
            {(formik) => {
                if (!submitted.current && formik.isSubmitting) submitted.current = true;
                if (dirtyFlag.current !== formik.dirty) {
                    dirtyFlag.current = formik.dirty;
                    // timeout allows the state update to run on the next tick after render
                    setTimeout(() => {
                        setFormDirty(formik.dirty);
                    }, 0);
                }
                return (
                    <Form id={formId} noValidate>
                        <div className="mb-2">
                            <div>
                                <b>Instructions</b>
                            </div>
                            <div className="ms-2">
                                {qualityGap.measure?.quality_measure?.instructions || 'None'}
                            </div>
                        </div>
                        {qualityGap.measure?.quality_measure &&
                            qualityGap.measure.quality_measure_details && (
                                <table className="gap-table quality-gap-table">
                                    <thead>
                                        <tr>
                                            <th className="checkbox-col">
                                                <span className="sr-only">Checkbox column</span>
                                            </th>
                                            <th className="w-30">Measure</th>
                                            <th>Acceptable Dates</th>
                                            <th className="w-15">Age</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <QualityActionRow
                                            measure={{
                                                ...qualityGap.measure.quality_measure,
                                                quality_measure_details:
                                                    qualityGap.measure.quality_measure_details!,
                                            }}
                                            submissionAnswer={qualityGap.submission_answer}
                                            handleFormInputChange={handleFormInputChange}
                                            isSingle={
                                                qualityGap.measure.quality_measure_details
                                                    ?.length === 1
                                            }
                                            readOnly={readOnly}
                                            statusFilter={statusFilter}
                                            formik={formik}
                                        />
                                    </tbody>
                                </table>
                            )}
                    </Form>
                );
            }}
        </Formik>
    );
}
